/**
 * The fields of a template, then its items one at a time behind a `<` / `>` pager.
 *
 * Shared with the template builder MCP app, which has no i18n: every word on it
 * arrives through `labels`.
 */
import { useId } from 'react'
import { ChevronLeft, ChevronRight, Database, PanelsTopLeft } from 'lucide-react'

import type { TemplateCategory, TemplateCategoryId } from '@/api/types'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { Textarea } from '@/shared/ui/textarea'
import type { DraftError, DraftErrors, DraftItem, TemplateDraft } from '../draft'

export interface DraftEditorLabels {
  title: string
  description: string
  whenToUse: string
  whenToUseHint: string
  categories: string
  items: string
  itemPosition: (position: number, count: number) => string
  previousItem: string
  nextItem: string
  itemName: string
  itemDescription: string
  includeRows: string
  includeRowsHint: string
  includeRowsFixed: string
  required: string
  requiredHint: string
  error: Record<DraftError, string>
}

export function TemplateDraftEditor({
  draft,
  onChange,
  errors,
  categories,
  labels,
  index,
  onIndexChange,
  rowsEditable,
}: {
  draft: TemplateDraft
  onChange: (draft: TemplateDraft) => void
  errors: DraftErrors
  categories: readonly TemplateCategory[]
  labels: DraftEditorLabels
  /** Which item the pager shows. */
  index: number
  onIndexChange: (index: number) => void
  /** Rows are chosen when a template is made and cannot be changed after. */
  rowsEditable: boolean
}) {
  const id = useId()
  const item = draft.items[index]

  function toggleCategory(category: TemplateCategoryId) {
    const categories = draft.categories.includes(category)
      ? draft.categories.filter((one) => one !== category)
      : [...draft.categories, category]
    onChange({ ...draft, categories })
  }

  function changeItem(values: Partial<DraftItem>) {
    onChange({
      ...draft,
      items: draft.items.map((one, at) => (at === index ? { ...one, ...values } : one)),
    })
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Field
          id={`${id}-title`}
          label={labels.title}
          error={errors.title}
          labels={labels}
        >
          <Input
            id={`${id}-title`}
            value={draft.title}
            aria-invalid={errors.title !== undefined}
            onChange={(event) => onChange({ ...draft, title: event.target.value })}
          />
        </Field>
        <Field
          id={`${id}-description`}
          label={labels.description}
          error={errors.description}
          labels={labels}
        >
          <Textarea
            id={`${id}-description`}
            value={draft.description}
            aria-invalid={errors.description !== undefined}
            onChange={(event) => onChange({ ...draft, description: event.target.value })}
          />
        </Field>
        <Field
          id={`${id}-when`}
          label={labels.whenToUse}
          hint={labels.whenToUseHint}
          error={errors.whenToUse}
          labels={labels}
        >
          <Textarea
            id={`${id}-when`}
            value={draft.whenToUse}
            aria-invalid={errors.whenToUse !== undefined}
            onChange={(event) => onChange({ ...draft, whenToUse: event.target.value })}
          />
        </Field>
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">{labels.categories}</legend>
          <div className="flex flex-wrap gap-1.5">
            {categories.map((category) => {
              const pressed = draft.categories.includes(category.id as TemplateCategoryId)
              return (
                <Button
                  key={category.id}
                  type="button"
                  size="sm"
                  variant={pressed ? 'default' : 'outline'}
                  aria-pressed={pressed}
                  className="h-7 rounded-full"
                  onClick={() => toggleCategory(category.id as TemplateCategoryId)}
                >
                  {category.label}
                </Button>
              )
            })}
          </div>
        </fieldset>
      </div>

      {item !== undefined && (
        <section aria-label={labels.items} className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center gap-2">
            {item.type === 'dashboard' ? (
              <PanelsTopLeft className="size-4 shrink-0" />
            ) : (
              <Database className="size-4 shrink-0" />
            )}
            <span className="text-sm font-medium" aria-live="polite">
              {labels.itemPosition(index + 1, draft.items.length)}
            </span>
            {item.required && (
              <Badge variant="secondary" title={labels.requiredHint}>
                {labels.required}
              </Badge>
            )}
            <div className="ml-auto flex gap-1">
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="size-8"
                aria-label={labels.previousItem}
                disabled={index === 0}
                onClick={() => onIndexChange(index - 1)}
              >
                <ChevronLeft />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="size-8"
                aria-label={labels.nextItem}
                disabled={index === draft.items.length - 1}
                onClick={() => onIndexChange(index + 1)}
              >
                <ChevronRight />
              </Button>
            </div>
          </div>

          <Field
            id={`${id}-item-name`}
            label={labels.itemName}
            error={errors.items[index]?.name}
            labels={labels}
          >
            <Input
              id={`${id}-item-name`}
              value={item.name}
              aria-invalid={errors.items[index]?.name !== undefined}
              onChange={(event) => changeItem({ name: event.target.value })}
            />
          </Field>
          <Field
            id={`${id}-item-description`}
            label={labels.itemDescription}
            labels={labels}
          >
            <Textarea
              id={`${id}-item-description`}
              value={item.description}
              onChange={(event) => changeItem({ description: event.target.value })}
            />
          </Field>
          {item.type === 'database' && (
            <div className="flex items-start gap-3">
              <Switch
                id={`${id}-item-rows`}
                checked={item.includeRows}
                disabled={!rowsEditable}
                onCheckedChange={(includeRows) => changeItem({ includeRows })}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <Label htmlFor={`${id}-item-rows`}>{labels.includeRows}</Label>
                <p className="text-xs text-muted-foreground">
                  {rowsEditable ? labels.includeRowsHint : labels.includeRowsFixed}
                </p>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}

function Field({
  id,
  label,
  hint,
  error,
  labels,
  children,
}: {
  id: string
  label: string
  hint?: string
  error?: DraftError | undefined
  labels: DraftEditorLabels
  children: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
      {children}
      {error !== undefined && (
        <p className="text-sm text-destructive">{labels.error[error]}</p>
      )}
    </div>
  )
}
